import { useState, useEffect, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

// ========== Countdown Component'i ==========
const CountdownTimer = memo(({ seconds, canResend, onResend, onTick }) => {
    const [countdown, setCountdown] = useState(seconds);
    const timerRef = useRef(null);

    useEffect(() => {
        setCountdown(seconds);
    }, [seconds]);

    useEffect(() => {
        if (countdown > 0) {
            timerRef.current = setInterval(() => {
                setCountdown(prev => {
                    const next = prev - 1;
                    if (next <= 0) {
                        clearInterval(timerRef.current);
                        onTick?.(true);
                        return 0;
                    }
                    onTick?.(false);
                    return next;
                });
            }, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    const formatTime = (sec) => {
        const mins = Math.floor(sec / 60);
        const secs = sec % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (countdown <= 0 && canResend) {
        return (
            <button
                type="button"
                onClick={onResend}
                className="text-[#FFB947] text-sm font-bold hover:underline"
            >
                ارسال مجدد
            </button>
        );
    }

    return (
        <span className="text-[#003373] text-sm font-bold">
            {formatTime(countdown)}
        </span>
    );
});

CountdownTimer.displayName = 'CountdownTimer';

// ========== OTP Input Component'i ==========
const OtpInputs = memo(({ otpCode, onChange, onKeyDown }) => {
    const inputRefs = useRef([]);

    useEffect(() => {
        setTimeout(() => {
            inputRefs.current[0]?.focus();
        }, 100);
    }, []);

    return (
        <div
            dir="ltr"
            style={{ direction: "ltr" }}
            className="flex flex-row justify-center gap-2"
        >
            {otpCode.map((digit, index) => (
                <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    dir="ltr"
                    style={{
                        direction: "ltr",
                        unicodeBidi: "plaintext",
                        textAlign: "center"
                    }}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => onChange(index, e, inputRefs)}
                    onKeyDown={(e) => onKeyDown(index, e, inputRefs)}
                    autoComplete="one-time-code"
                    className="w-11 h-14 sm:w-12 sm:h-16 rounded-xl border-2 border-[#EBEBEB] text-center text-xl font-bold text-[#003373] focus:border-[#FFB947] focus:outline-none"
                />
            ))}
        </div>
    );
});

OtpInputs.displayName = 'OtpInputs';

// ========== Ana Auth Component ==========
const Auth = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();

    const [activeTab, setActiveTab] = useState('login');

    // Login state
    const [loginPhone, setLoginPhone] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [useOtpLogin, setUseOtpLogin] = useState(false);

    // Register state
    const [registerPhone, setRegisterPhone] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);

    // OTP state
    const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
    const [otpSent, setOtpSent] = useState(false);
    const [countdownKey, setCountdownKey] = useState(0);
    const [canResend, setCanResend] = useState(false);
    const [otpMessage, setOtpMessage] = useState('');
    const [otpPhone, setOtpPhone] = useState('');

    // Validation
    const [loginPhoneValid, setLoginPhoneValid] = useState(false);
    const [loginPasswordValid, setLoginPasswordValid] = useState(false);
    const [registerPhoneValid, setRegisterPhoneValid] = useState(false);
    const [registerPasswordValid, setRegisterPasswordValid] = useState(false);

    // Loading & Error
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Validation fonksiyonları
    const validatePhone = (phone) => {
        const cleaned = phone.replace(/\D/g, '');
        return cleaned.length >= 10;
    };

    const formatPhoneForApi = (phone) => {
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.startsWith('0')) return cleaned.substring(1);
        if (cleaned.startsWith('98')) return cleaned.substring(2);
        return cleaned;
    };

    const validatePassword = (pass) => pass.length >= 6;

    // Handle changes
    const handleLoginPhoneChange = (e) => {
        const value = e.target.value;
        setLoginPhone(value);
        setLoginPhoneValid(validatePhone(value));
    };

    const handleRegisterPhoneChange = (e) => {
        const value = e.target.value;
        setRegisterPhone(value);
        setRegisterPhoneValid(validatePhone(value));
    };

    const handleLoginPasswordChange = (e) => {
        const value = e.target.value;
        setLoginPassword(value);
        setLoginPasswordValid(validatePassword(value));
    };

    const handleRegisterPasswordChange = (e) => {
        const value = e.target.value;
        setRegisterPassword(value);
        setRegisterPasswordValid(validatePassword(value));
    };

    // OTP handlers - SOLDAN SAĞA OTOMATİK FOCUS
    const handleOtpChange = (index, e, inputRefs) => {
        const value = e.target.value;
        
        // Sadece rakam kabul et
        if (value && !/^\d+$/.test(value)) return;

        const newOtp = [...otpCode];

        // Yapıştırma (paste) durumu
        if (value.length > 1) {
            const digits = value.replace(/\D/g, '').split('').slice(0, 6);
            for (let i = 0; i < 6; i++) {
                newOtp[i] = digits[i] || '';
            }
            setOtpCode(newOtp);
            
            // Son dolu input'un bir sonrasına veya son input'a focus
            const lastFilledIndex = digits.length >= 6 ? 5 : digits.length;
            if (lastFilledIndex < 6) {
                inputRefs.current[lastFilledIndex]?.focus();
            }
            return;
        }

        // Tek karakter girişi
        newOtp[index] = value;
        setOtpCode(newOtp);

        // SOLDAN SAĞA: Sonraki input'a otomatik geç
        if (value && index < 5) {
            // Bir sonraki input'a focus
            setTimeout(() => {
                inputRefs.current[index + 1]?.focus();
            }, 10);
        }
    };

    const handleOtpKeyDown = (index, e, inputRefs) => {
        // Backspace - önceki input'a dön
        if (e.key === 'Backspace') {
            e.preventDefault();
            const newOtp = [...otpCode];
            
            if (otpCode[index]) {
                // Mevcut input doluysa temizle
                newOtp[index] = '';
                setOtpCode(newOtp);
            } else if (index > 0) {
                // Boş inputta backspace → önceki input'a git ve temizle
                newOtp[index - 1] = '';
                setOtpCode(newOtp);
                setTimeout(() => {
                    inputRefs.current[index - 1]?.focus();
                }, 10);
            }
            return;
        }

        // Sol ok tuşu → önceki input (LTR'de sol = geri)
        if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            inputRefs.current[index - 1]?.focus();
        }

        // Sağ ok tuşu → sonraki input (LTR'de sağ = ileri)
        if (e.key === 'ArrowRight' && index < 5) {
            e.preventDefault();
            inputRefs.current[index + 1]?.focus();
        }
    };

    // API calls
    const handleRegisterOtpSend = async () => {
        if (!registerPhoneValid || !registerPasswordValid) return;
        setLoading(true);
        setError('');
        try {
            const formattedPhone = formatPhoneForApi(registerPhone);
            setOtpPhone(formattedPhone);
            await authService.registerOtpSend(formattedPhone, registerPassword);
            setOtpSent(true);
            setCountdownKey(prev => prev + 1);
            setCanResend(false);
            setOtpMessage('کد تایید ارسال شد');
        } catch (err) {
            setError(err.response?.data?.message || 'خطا در ارسال کد');
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterOtpVerify = async () => {
        const otp = otpCode.join('');
        if (otp.length !== 6) return;
        setLoading(true);
        setError('');
        try {
            await authService.registerOtpVerify(otpPhone, otp);
            navigate('/');
        } catch (err) {
            setError(err.response?.data?.message || 'کد نامعتبر');
        } finally {
            setLoading(false);
        }
    };

    const handleLoginOtpSend = async () => {
        if (!loginPhoneValid) return;
        setLoading(true);
        setError('');
        try {
            const formattedPhone = formatPhoneForApi(loginPhone);
            setOtpPhone(formattedPhone);
            await authService.loginOtpSend(formattedPhone);
            setOtpSent(true);
            setCountdownKey(prev => prev + 1);
            setCanResend(false);
            setOtpMessage('کد تایید ارسال شد');
        } catch (err) {
            setError(err.response?.data?.message || 'خطا در ارسال کد');
        } finally {
            setLoading(false);
        }
    };

    const handleLoginOtpVerify = async () => {
        const otp = otpCode.join('');
        if (otp.length !== 6) return;
        setLoading(true);
        setError('');
        try {
            await authService.loginOtpVerify(otpPhone, otp);
            navigate('/');
        } catch (err) {
            setError(err.response?.data?.message || 'کد نامعتبر');
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async () => {
        if (!loginPhoneValid || !loginPasswordValid) return;
        setLoading(true);
        setError('');
        try {
            const formattedPhone = formatPhoneForApi(loginPhone);
            await authService.login(formattedPhone, loginPassword);
            navigate('/');
        } catch (err) {
            setError(err.response?.data?.message || 'خطا در ورود');
        } finally {
            setLoading(false);
        }
    };

    const resetOtp = () => {
        setOtpSent(false);
        setOtpCode(['', '', '', '', '', '']);
        setCanResend(false);
        setOtpMessage('');
        setError('');
        setOtpPhone('');
        setCountdownKey(prev => prev + 1);
    };

    const handleOtpModeSwitch = () => {
        setUseOtpLogin(!useOtpLogin);
        resetOtp();
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-8 font-iransansRegular bg-gradient-to-br from-gray-50 to-gray-100">
            <motion.div
                className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 pt-16 sm:pt-20"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
            >
                {/* Profil Icon */}
                <div className="absolute -top-[65px] left-1/2 -translate-x-1/2">
                    <motion.img
                        src="/user.png"
                        alt="User"
                        className="w-[129px] h-[129px] rounded-full object-cover border-4 border-white shadow-lg"
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, type: "spring" }}
                    />
                </div>

                {/* Tab Buttons */}
                <div className="flex bg-[#EFEFFF] rounded-xl p-1 mb-6">
                    <button
                        onClick={() => { setActiveTab('login'); resetOtp(); }}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 ${activeTab === 'login'
                            ? 'bg-[#003373] text-white shadow-md'
                            : 'text-[#003373] hover:bg-white/50'
                            }`}
                    >
                        {language === 'fa' ? 'ورود' : 'Login'}
                    </button>
                    <button
                        onClick={() => { setActiveTab('register'); resetOtp(); }}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 ${activeTab === 'register'
                            ? 'bg-[#003373] text-white shadow-md'
                            : 'text-[#003373] hover:bg-white/50'
                            }`}
                    >
                        {language === 'fa' ? 'ثبت‌نام' : 'Register'}
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <motion.div
                        className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 mb-4 text-sm"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        {error}
                    </motion.div>
                )}

                {/* Success Message */}
                {otpMessage && (
                    <motion.div
                        className="bg-green-50 border border-green-200 text-green-600 rounded-xl px-4 py-3 mb-4 text-sm"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        {otpMessage}
                    </motion.div>
                )}

                <AnimatePresence mode="wait">
                    {/* LOGIN TAB */}
                    {activeTab === 'login' && (
                        <motion.div
                            key="login"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Phone Input */}
                            <div className="mb-4">
                                <label className="block text-[#204286] font-bold text-xs mb-1.5">
                                    {language === 'fa' ? 'تلفن همراه' : 'Phone'}
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                                        <span className="text-[#003373] font-bold text-sm bg-[#EFEFFF] px-2 py-1 rounded-lg">+98</span>
                                    </div>
                                    <input
                                        type="tel"
                                        value={loginPhone}
                                        onChange={handleLoginPhoneChange}
                                        placeholder="912 345 6789"
                                        className={`w-full px-4 py-3 rounded-xl border-2 text-[#204286] font-medium text-sm transition-all pl-16 ${loginPhoneValid
                                            ? 'border-[#003373]'
                                            : loginPhone
                                                ? 'border-red-300'
                                                : 'border-[#EBEBEB] focus:border-[#003373]'
                                            } focus:outline-none`}
                                    />
                                    {loginPhoneValid && (
                                        <motion.div
                                            className="absolute -left-6 top-1/2 -translate-y-1/2"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <svg className="w-5 h-5 text-[#003373]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </motion.div>
                                    )}
                                </div>
                            </div>

                            {/* Password Input */}
                            {!useOtpLogin && (
                                <div className="mb-4">
                                    <label className="block text-[#204286] font-bold text-xs mb-1.5">
                                        {language === 'fa' ? 'رمز عبور' : 'Password'}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={loginPassword}
                                            onChange={handleLoginPasswordChange}
                                            placeholder="••••••••"
                                            className={`w-full px-4 py-3 rounded-xl border-2 text-[#204286] font-medium text-sm transition-all pl-10 ${loginPasswordValid
                                                ? 'border-[#003373]'
                                                : loginPassword
                                                    ? 'border-red-300'
                                                    : 'border-[#EBEBEB] focus:border-[#003373]'
                                                } focus:outline-none`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute left-3 top-1/2 -translate-y-1/2"
                                        >
                                            {showPassword ? (
                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                </svg>
                                            )}
                                        </button>
                                        {loginPasswordValid && (
                                            <motion.div
                                                className="absolute -left-6 top-1/2 -translate-y-1/2"
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                <svg className="w-5 h-5 text-[#003373]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </motion.div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* OTP Link */}
                            <div className="text-center mb-4">
                                <button
                                    onClick={handleOtpModeSwitch}
                                    className="text-[#003373] text-xs font-medium hover:text-[#FFB947] transition-colors"
                                >
                                    {useOtpLogin
                                        ? (language === 'fa' ? 'ورود با رمز عبور' : 'Login with Password')
                                        : (language === 'fa' ? 'رمز عبور خود را فراموش کرده‌ام ؟ / ورود با کد یکبار مصرف' : 'Forgot Password? / Login with OTP')
                                    }
                                </button>
                            </div>

                            {/* Buttons */}
                            {!otpSent ? (
                                <>
                                    {useOtpLogin ? (
                                        <div className="flex gap-3">
                                            <button onClick={handleOtpModeSwitch} className="flex-1 py-3 rounded-xl font-bold text-sm border-2 border-[#EBEBEB] text-[#003373] hover:bg-gray-50 transition-all">
                                                {language === 'fa' ? 'برگشت' : 'Back'}
                                            </button>
                                            <button
                                                onClick={handleLoginOtpSend}
                                                disabled={loading || !loginPhoneValid}
                                                className={`flex-[2] py-3 rounded-xl font-bold text-sm transition-all ${loading || !loginPhoneValid ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#FFB947] text-white hover:bg-[#e5a63a] hover:shadow-lg'}`}
                                            >
                                                {loading ? (
                                                    <span className="flex items-center justify-center gap-2">
                                                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                                    </span>
                                                ) : (language === 'fa' ? 'ارسال کد' : 'Send Code')}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={handleLogin}
                                            disabled={loading || !loginPhoneValid || !loginPasswordValid}
                                            className={`w-full py-3 rounded-xl font-bold text-sm transition-all duration-300 ${loading || !loginPhoneValid || !loginPasswordValid ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#FFB947] text-white hover:bg-[#e5a63a] hover:shadow-lg'}`}
                                        >
                                            {loading ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                                    {language === 'fa' ? 'در حال پردازش...' : 'Processing...'}
                                                </span>
                                            ) : (language === 'fa' ? 'ورود' : 'Login')}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    {/* OTP Input */}
                                    <div className="mb-4">
                                        <label className="block text-[#204286] font-bold text-xs mb-3 text-center">
                                            {language === 'fa' ? 'کد تایید را وارد کنید' : 'Enter verification code'}
                                        </label>
                                        <OtpInputs
                                            otpCode={otpCode}
                                            onChange={handleOtpChange}
                                            onKeyDown={handleOtpKeyDown}
                                        />
                                    </div>

                                    {/* Countdown */}
                                    <div className="text-center mb-4">
                                        <CountdownTimer
                                            key={countdownKey}
                                            seconds={90}
                                            canResend={canResend}
                                            onResend={() => {
                                                setCanResend(false);
                                                setCountdownKey(prev => prev + 1);
                                                handleLoginOtpSend();
                                            }}
                                            onTick={(val) => setCanResend(val)}
                                        />
                                    </div>

                                    {/* OTP Buttons */}
                                    <div className="flex gap-3">
                                        <button onClick={resetOtp} className="flex-1 py-3 rounded-xl font-bold text-sm border-2 border-[#EBEBEB] text-[#003373] hover:bg-gray-50 transition-all">
                                            {language === 'fa' ? 'بازگشت' : 'Back'}
                                        </button>
                                        <button
                                            onClick={handleLoginOtpVerify}
                                            disabled={otpCode.join('').length !== 6 || loading}
                                            className={`flex-[2] py-3 rounded-xl font-bold text-sm transition-all ${otpCode.join('').length === 6 && !loading ? 'bg-[#FFB947] text-white hover:bg-[#e5a63a] hover:shadow-lg' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                                        >
                                            {loading ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                                </span>
                                            ) : (language === 'fa' ? 'تایید کد' : 'Verify')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </motion.div>
                    )}

                    {/* REGISTER TAB */}
                    {activeTab === 'register' && (
                        <motion.div
                            key="register"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            {!otpSent ? (
                                <>
                                    {/* Phone Input */}
                                    <div className="mb-4">
                                        <label className="block text-[#204286] font-bold text-xs mb-1.5">
                                            {language === 'fa' ? 'تلفن همراه' : 'Phone'}
                                        </label>
                                        <div className="relative">
                                            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                                                <span className="text-[#003373] font-bold text-sm bg-[#EFEFFF] px-2 py-1 rounded-lg">+98</span>
                                            </div>
                                            <input
                                                type="tel"
                                                value={registerPhone}
                                                onChange={handleRegisterPhoneChange}
                                                placeholder="912 345 6789"
                                                className={`w-full px-4 py-3 rounded-xl border-2 text-[#204286] font-medium text-sm transition-all pl-16 ${registerPhoneValid ? 'border-[#003373]' : registerPhone ? 'border-red-300' : 'border-[#EBEBEB] focus:border-[#003373]'} focus:outline-none`}
                                            />
                                            {registerPhoneValid && (
                                                <motion.div className="absolute -left-6 top-1/2 -translate-y-1/2" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300 }}>
                                                    <svg className="w-5 h-5 text-[#003373]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                                </motion.div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Password Input */}
                                    <div className="mb-4">
                                        <label className="block text-[#204286] font-bold text-xs mb-1.5">
                                            {language === 'fa' ? 'رمز عبور' : 'Password'}
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showRegisterPassword ? 'text' : 'password'}
                                                value={registerPassword}
                                                onChange={handleRegisterPasswordChange}
                                                placeholder="••••••••"
                                                className={`w-full px-4 py-3 rounded-xl border-2 text-[#204286] font-medium text-sm transition-all pl-10 ${registerPasswordValid ? 'border-[#003373]' : registerPassword ? 'border-red-300' : 'border-[#EBEBEB] focus:border-[#003373]'} focus:outline-none`}
                                            />
                                            <button type="button" onClick={() => setShowRegisterPassword(!showRegisterPassword)} className="absolute left-3 top-1/2 -translate-y-1/2">
                                                {showRegisterPassword ? (
                                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                                ) : (
                                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                                                )}
                                            </button>
                                            {registerPasswordValid && (
                                                <motion.div className="absolute -left-6 top-1/2 -translate-y-1/2" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300 }}>
                                                    <svg className="w-5 h-5 text-[#003373]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                                </motion.div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Register Button */}
                                    <button
                                        onClick={handleRegisterOtpSend}
                                        disabled={loading || !registerPhoneValid || !registerPasswordValid}
                                        className={`w-full py-3 rounded-xl font-bold text-sm transition-all duration-300 ${loading || !registerPhoneValid || !registerPasswordValid ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#FFB947] text-white hover:bg-[#e5a63a] hover:shadow-lg'}`}
                                    >
                                        {loading ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                                {language === 'fa' ? 'در حال پردازش...' : 'Processing...'}
                                            </span>
                                        ) : (language === 'fa' ? 'ثبت‌نام' : 'Register')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    {/* OTP Input */}
                                    <div className="mb-4">
                                        <label className="block text-[#204286] font-bold text-xs mb-3 text-center">
                                            {language === 'fa' ? 'کد تایید را وارد کنید' : 'Enter verification code'}
                                        </label>
                                        <OtpInputs
                                            otpCode={otpCode}
                                            onChange={handleOtpChange}
                                            onKeyDown={handleOtpKeyDown}
                                        />
                                    </div>

                                    {/* Countdown */}
                                    <div className="text-center mb-4">
                                        <CountdownTimer
                                            key={countdownKey}
                                            seconds={90}
                                            canResend={canResend}
                                            onResend={() => {
                                                setCanResend(false);
                                                setCountdownKey(prev => prev + 1);
                                                handleRegisterOtpSend();
                                            }}
                                            onTick={(val) => setCanResend(val)}
                                        />
                                    </div>

                                    {/* OTP Buttons */}
                                    <div className="flex gap-3">
                                        <button onClick={resetOtp} className="flex-1 py-3 rounded-xl font-bold text-sm border-2 border-[#EBEBEB] text-[#003373] hover:bg-gray-50 transition-all">
                                            {language === 'fa' ? 'بازگشت' : 'Back'}
                                        </button>
                                        <button
                                            onClick={handleRegisterOtpVerify}
                                            disabled={otpCode.join('').length !== 6 || loading}
                                            className={`flex-[2] py-3 rounded-xl font-bold text-sm transition-all ${otpCode.join('').length === 6 && !loading ? 'bg-[#FFB947] text-white hover:bg-[#e5a63a] hover:shadow-lg' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                                        >
                                            {loading ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                                </span>
                                            ) : (language === 'fa' ? 'تایید کد' : 'Verify')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default Auth;